/**
 * "Fix what was cited" (owner, 2026-10-13: "Loki has a problem with flies in their last health check… what to do in a sleek way";
 * then "generalize… how CleanCleaver will help it stay better permanently… watch for what and ensure the sources are fixed").
 * One shape for any cited item, four parts, one line per step and one tap per line:
 *   1. now      Fix it today: the steps that take it away today.
 *   2. source   Fix where it comes from: the cause, each with a proof tap ("Yes, it closes on its own"), kept for the binder.
 *   3. watch    Watch for it: the recurring check that keeps it fixed ("We'll ask about flies at every opening and closing"),
 *               turned on in one tap when it isn't in the plan yet.
 *   4. recheck  Check back: a dated question (7, then 30 days): "No flies" twice in a row closes it with a reward line; "I saw
 *               flies" reopens part 2 (the way in needs another look) and says to call the licensed pest company.
 * An item cited at more than one inspection we hold opens with part 2 (the cause is what came back).
 *
 * Data-driven: CITED_PLANS holds one plan per cited thing, picked by FDA Form 3-A item AND the department's own words. Only the
 * flies plan ships (owner review: food-safety claims need it); other items keep today's behaviour. Drafts for the rest:
 * docs/compliance/CITED-FIX-PLANS.md.
 *
 * Every rule we state cites its source (test: packages/core/test/citedplan.test.ts):
 *   law     an FDA Food Code 2022 section, with its words quoted from docs/source (the file named), cited at the place through
 *           roomwalk.ts walkRuleAt (Utah: "adopted by R392-100-4(1)(b)"), plus the county's own code number where its code book
 *           maps it (Salt Lake County Health Regulation #5: 4.6.15, 4.6.50, 4.6.51; states/ut/official.ts SLCO_CODE_FDA).
 *   advice  our stricter advice, shown apart from the law and labeled ours, quoted from our source protocol in docs/source.
 * Never a grade, a pass or a ranking; nothing we write says "official" (roomwalk.ts JUDGEMENT_WORDS; the test's word lists).
 * Staff never spray or bait: the step says to call the licensed pest company (library "pest-signs" "What NOT to do"). The law
 * (7-202.12(C)) limits restricted-use pesticides to certified applicators; "staff never spray" is our stricter advice, said apart.
 * The library's "pest-prevention" topic has the same steps as "Flies inside right now", and the plan links to it.
 * "Get what you need" (owner, 2026-10-13: "Amazon search ready to help get rid of flies button"): a step that needs a thing to buy
 * carries `supplies` (a plain name, an Amazon SEARCH, a placement line); the view gives each step its links and says SUPPLY_NOTE once.
 * Never a pesticide, bait or insect light trap (SUPPLY_NEVER), never a brand, never an affiliate tag (appevents.ts UNTAGGED_PLACEMENTS).
 * Off means off: nothing here sends an email or a push. The check-back shows in the app only (Today's quiet row, /inspection).
 *
 * Pure: the API stores CitedPlanDoc (services/api/src/official/citedplans.ts, migration 0148 cited_plans) and passes the docs in.
 */
import { shortDate } from "./official.ts";
import { countyCodeLine, stateLawLine } from "./states/ut/official.ts";
import { walkRuleAt } from "./roomwalk.ts";
import type { PlaceRef } from "./placecode.ts";
import type { TaskCategory } from "./model.ts";
import type { FixLine } from "./fixplans.ts";
import { itemFixOf } from "./fixplans-registry.ts";
import "./fixplans-eager.ts"; // registers the fix-plan data; the web build swaps it for fixplans-none.ts (apps/web/vite.config.ts) and loads it on demand
import { anonymizeNote, matchNote } from "./violationnotes.ts";
import type { TicketAsk } from "./content.ts";

// ── The plan's data ──────────────────────────────────────────────────────────────────────────────────────────────────────

/** A Food Code section, its words quoted from a file under docs/source (test: the quote is in that file). */
export interface PlanLaw {
  cite: string;
  /** The rich plans (flies) quote the section's words from a file under docs/source; the item plans cite the section only (its words
   *  are in docs/source/research/paa/inspection-form-items.txt, test-checked). */
  quote?: string;
  quoteFile?: string;
}
/** Our advice, stricter than the law, quoted from our source protocol (test: the quote is in that file). */
export interface PlanAdvice { say: string; quote?: string; quoteFile?: string }
export interface PlanStepDef {
  id: string;
  /** One line, the action first, 12 words at most (easy.ts lintEasy). */
  say: string;
  law?: PlanLaw;
  advice?: readonly PlanAdvice[];
  /** "Get what you need" (owner, 2026-10-13): a thing to buy for this step, as an Amazon search. Only steps that need one. */
  supplies?: readonly PlanSupply[];
  /** Guidance that is neither law nor ours: "FDA's guide for inspectors, item 3" (Guide 3-B). */
  basis?: string;
}

/**
 * A thing to buy for a step (owner, 2026-10-13: "Amazon search ready to help get rid of flies button"). A plain name and an Amazon
 * SEARCH for it (https://www.amazon.com/s?k=…): never a product page, a brand or an affiliate tag (the app's click listener never
 * tags the "plan-supply" placement: appevents.ts amazonTagFor), and never what the Food Code keeps for a licensed operator
 * (7-202.12: pesticides, sprays, baits) or an insect light trap (6-202.13 limits where one goes). SUPPLY_NEVER is the lint; the
 * test runs it over every supply. `note` is the one line on where it goes or how to use it.
 */
export interface PlanSupply { name: string; query: string; note?: string }
/** Never in a supply's name, search or note: what 7-202.12 keeps for a licensed operator, and insect light traps (6-202.13). */
export const SUPPLY_NEVER = /\b(pesticides?|insecticides?|sprays?|baits?|poisons?|fogg(?:er|ers|ing)|bug ?bombs?|aerosols?|larvicides?|rodenticides?|zappers?|electr(?:ic|ocut\w*)|(?:insect |bug |uv |fly )?lights?|uv|lamps?|repellents?)\b/i;
/** An Amazon search for the words, and nothing else (no tag, no product, no brand). */
export const supplyUrl = (query: string) => `https://www.amazon.com/s?k=${encodeURIComponent(query.trim()).replace(/%20/g, "+")}`;
/** The one line near the first "Get it" (owner, 2026-10-13). */
export const SUPPLY_NOTE = "Links search Amazon. We don't sell these or get paid for them.";
export const SUPPLY_LEAD = "Get it:";
/** The first-party count's control name for a step's supply links (lib/appEvents.ts counts data-cc taps): cc:supply-<step>. */
export const supplyControl = (stepId: string) => `supply-${stepId}`;
export interface PlanProofDef extends PlanStepDef {
  /** The proof tap's words: "Yes, it closes on its own". */
  yes: string;
}
export type PlanPart = "now" | "source" | "watch" | "recheck";
export interface CitedPlanDef {
  id: string;
  /** FDA Form 3-A items the cited code must be (official-riskfactors.ts fdaItemsOf). */
  items: readonly number[];
  /** The department's words that pick this plan, read from the inspector's own line (else the code's title). */
  words: RegExp;
  /** Words that mean it is something else (roaches, mice): today's behaviour stays. */
  notWords?: RegExp;
  /** "flies": said in headings and lines. */
  thing: string;
  /** The library topic with these steps in full (library.ts), and the section's heading. */
  guide: { id: string; section: string };
  now: readonly PlanStepDef[];
  source: readonly PlanProofDef[];
  watch: {
    /** Built-in checks (cadence.ts BASE_CHECKS) that watch for it, each with the line it is said as. Any one on counts. */
    base: readonly { key: string; line: string }[];
    /** The daily look "Turn it on" makes when no daily built-in is on (a checklist item, the same shape as lastinspection's Add it). */
    add: { title: string; how: string; category: TaskCategory; schedule: { perShift?: true; everyDays?: number }; requirementIds: string[]; line: string; button: string };
    /** Built-in keys that already ask DAILY (the add is not offered when one is on). */
    daily: readonly string[];
    /** A flow every business runs that watches it (the health check-in): said always, and nothing to add. */
    builtin?: string;
    /** Checklist item titles that already watch it (lastinspection.ts INSPECTION_COVER titles): one on, nothing to add. */
    titles?: readonly string[];
  };
  recheck: { days: readonly number[]; question: string; clear: string; seen: string; clearRun: number; seenLine: string };
  /** The plan's own words where they differ from the flies plan's (item plans: "Fix where it comes from"). Absent: FLIES_TEXT. */
  text?: PlanText;
  /** "Your plan for the flies"; an item plan: "Your plan: cold holding". */
  title?: string;
  /** A plan about a worker's health (items 3 and 4): its hand-off notice says nothing of the item or its steps. */
  quiet?: true;
  /** Which kind: the rich plan (flies), an item's plan (its situation, or its general plan), or the fallback from the citation's words. */
  kind?: PlanKind;
  /** The FDA item and the situation it was built from (item plans). */
  item?: number;
  situation?: string | null;
  /** "Ask in CleanCleaver" with the department's words (the fallback plan; any plan may carry it). */
  ask?: { label: string; to: string; q: string };
}

export type PlanKind = "rich" | "situation" | "general" | "fallback";

/** The words a plan says around its steps. FLIES_TEXT is the flies plan's, unchanged (test: its view equals the saved one). */
export interface PlanText {
  sourceHeading: string;
  repeatLede: string;
  /** After today's fixes, when the source part comes next. */
  nextSource: string;
  /** "The way in is closed" (no full stop). */
  sourceDone: string;
  /** "Flies were cited. Your 5-step plan is ready" (no full stop). */
  ready: (steps: number) => string;
  nowProgress: (done: number, total: number) => string;
  sourceProgress: (done: number, total: number) => string;
  /** The binder's trail. */
  trailSource: string;
  trailHistory: string;
  /** A "seen" answer in the list of answers ("flies seen"). */
  seenAnswer: string;
  /** "No flies reported since Oct 3." */
  closedSince: (date: string) => string;
  closedTail: (daily: boolean) => string;
  /** The prep hand-off preview's line. */
  handoff: { lede: string; then: string };
}

const FC = "research/round3/food-code-2022-round3-excerpts.txt";
const PROTO = "parish-food-safety-protocol.md";
const PESTS = "research/pests-food-code-2022-excerpts.txt";

/** The flies plan (FDA item 38, "No pests inside"), from Food Code 6-202.13, 6-202.15, 6-501.111 and our pest protocol. */
const FLIES: CitedPlanDef = {
  id: "flies",
  items: [38],
  words: /\b(fl(y|ies)|gnats?|insects?)\b/i,
  notWords: /\b(roach(es)?|cockroach(es)?|rodents?|mouse|mice|rats?|droppings|gnaw)\b/i,
  thing: "flies",
  guide: { id: "pest-prevention", section: "Flies inside right now" },
  // Inspect, then take away what feeds them, then catch the ones inside, then the licensed company (owner, 2026-10-13: "we need
  // steps to remove flies"). Keeping new ones out is part 2.
  now: [
    { id: "find", say: "Find where flies gather: trash, drains, mop sink, produce, back door.",
      law: { cite: "Food Code 6-501.111(B)", quote: "Routinely inspecting the PREMISES for evidence of pests;", quoteFile: FC },
      advice: [{ say: "Fruit flies gather near produce, drains, the mop sink and recycling.", quote: "Fruit flies near produce, drains, mop sink or recycling", quoteFile: PROTO },
        { say: "Act within 24 hours when you see more than an occasional fly.", quote: "More than an occasional fly in the kitchen, or any fly breeding: action within 24 hours", quoteFile: PROTO }] },
    { id: "trash", say: "Take out the trash, wash the cans, and cover ripe produce.",
      law: { cite: "Food Code 6-501.111(D)", quote: "Eliminating harborage conditions.", quoteFile: FC },
      advice: [{ say: "Empty the trash and wash the cans every day.", quote: "trash emptied and cans washed daily", quoteFile: PROTO }] },
    { id: "drains", say: "Scrub drains and the mop sink. Hang mops to dry.",
      law: { cite: "Food Code 6-501.111(D)", quote: "Eliminating harborage conditions.", quoteFile: FC },
      advice: [{ say: "Keep floor drains clean. Leave no standing water, and hang mops to dry.", quote: "no standing water; dry mops hung up", quoteFile: PROTO }],
      supplies: [{ name: "Drain brush", query: "drain brush commercial kitchen" },
        { name: "Drain cleaner labeled for food service drains", query: "enzyme drain cleaner food service", note: "Use it as the label says." }] },
    { id: "traps", say: "Set fly traps away from food, prep tables and clean dishes.",
      law: { cite: "Food Code 6-202.13(B)(2)", quote: "Dead insects and insect fragments are prevented from being impelled onto or falling on exposed FOOD; clean EQUIPMENT, UTENSILS, and LINENS;", quoteFile: FC },
      advice: [{ say: "Use traps and glue boards. Never hang one over food or clean equipment.", quote: "traps, glue boards, insect light traps (never over food or clean equipment)", quoteFile: PROTO }],
      supplies: [{ name: "Fly traps for kitchens", query: "sticky fly traps fruit fly traps kitchen", note: "Set them away from food, prep areas and clean dishes." }] },
    { id: "call", say: "Call your licensed pest company. Staff never spray or bait.",
      law: { cite: "Food Code 7-202.12(C)", quote: "A restricted USE PESTICIDE shall be applied only by an applicator certified", quoteFile: PESTS },
      advice: [{ say: "Only a licensed pest control operator applies pesticides. Staff never do, even with a store-bought spray.", quote: "applied only by a licensed, certified pest control operator", quoteFile: PROTO },
        { say: "Call them the same day if flies keep coming back.", quote: "repeated flies or stored-product insects, means immediate action: protect or discard affected food, clean the area, and call the pest control operator the same day", quoteFile: PROTO }] },
  ],
  source: [
    { id: "door-closes", say: "Check the back door closes on its own.", yes: "Yes, it closes on its own",
      law: { cite: "Food Code 6-202.15(A)(3)", quote: "Solid, self-closing, tight-fitting doors.", quoteFile: FC },
      advice: [{ say: "Keep outside doors closed, even between deliveries.", quote: "keep doors closed; air curtains or screens on doors propped for deliveries", quoteFile: PROTO }],
      supplies: [{ name: "Self-closing door hinge or closer", query: "self closing door closer commercial" }] },
    { id: "door-sweep", say: "Check for a door sweep with no gap underneath.", yes: "Yes, the sweep is on",
      law: { cite: "Food Code 6-202.15(A)(1)", quote: "Filling or closing holes and other gaps along floors, walls, and ceilings;", quoteFile: FC },
      advice: [{ say: "Put a door sweep on every outside door.", quote: "Exterior doors self-closing and tight-fitting, with door sweeps; no gaps", quoteFile: PROTO }],
      supplies: [{ name: "Door sweep", query: "door sweep exterior commercial door", note: "For the back door." }] },
    { id: "drains", say: "Check the floor drains are clean, with covers on.", yes: "Yes, clean and covered",
      law: { cite: "Food Code 6-501.111(D)", quote: "Eliminating harborage conditions.", quoteFile: FC },
      advice: [{ say: "Keep floor drains clean, with covers in place.", quote: "Floor drains clean with covers in place", quoteFile: PROTO }] },
    { id: "screens", say: "Check screens on any window or door kept open.", yes: "Yes, or none are kept open",
      law: { cite: "Food Code 6-202.15(D)(1)", quote: "16 mesh to 25.4 mm (16 mesh to 1 inch) screens;", quoteFile: FC },
      advice: [{ say: "Use an air curtain or a screen on a door propped open.", quote: "air curtains or screens on doors propped for deliveries", quoteFile: PROTO }],
      supplies: [{ name: "Window screen, 16 mesh", query: "16 mesh window screen" }] },
    { id: "fly-light", say: "Check fly lights and strips hang away from food and prep.", yes: "Yes, or we have none",
      law: { cite: "Food Code 6-202.13(B)(1)", quote: "The devices are not located over a FOOD preparation area; and", quoteFile: FC } },
  ],
  watch: {
    base: [
      { key: "open-pest-look", line: "We'll ask about flies at every opening and closing." },
      { key: "close-pest-look", line: "We'll ask about flies at every opening and closing." },
      { key: "pest-walkthrough", line: "We'll ask about insects every week, on the pest walk-through." },
    ],
    daily: ["open-pest-look", "close-pest-look"],
    add: {
      title: "Look for flies at the trash, drains and back door",
      how: "Look at the trash, drains, mop sink, produce and back door. Saw flies? Tap Report and say where.",
      category: "pest", schedule: { perShift: true }, requirementIds: ["fc-pest-control"],
      line: "We'll ask about flies every shift.", button: "Turn on a daily fly look",
    },
  },
  recheck: {
    days: [7, 30], question: "Any flies this week?", clear: "No flies", seen: "I saw flies", clearRun: 2,
    seenLine: "Flies came back, so the way in needs another look. Call your licensed pest company today.",
  },
};

/** The richer plans (today: flies). Every other cited item gets its item plan (itemPlanDef), built from core fixplans.ts. */
export const CITED_PLANS: readonly CitedPlanDef[] = [FLIES];

/** The plan for one cited item: its FDA items and the department's words (the inspector's line first, else the title). */
export function citedPlanFor(items: readonly number[], words: { title: string; detail: string | null }): CitedPlanDef | null {
  const text = words.detail?.trim() || words.title;
  return CITED_PLANS.find((p) => p.items.some((n) => items.includes(n)) && p.words.test(text) && !p.notWords?.test(text)) ?? null;
}

// ── What is stored (one per place, plan and cited inspection) ────────────────────────────────────────────────────────────

export interface PlanTap { at: string; by: string; byName: string }
export interface PlanAnswer extends PlanTap { answer: "clear" | "seen" }
export interface CitedPlanDoc {
  /** citedPlanId(locationId, planId, inspection date, code). */
  id: string;
  orgId: string;
  locationId: string;
  planId: string;
  code: string;
  citedOn: string;
  /** The first tap: the check-backs are dated from here. */
  startedAt: string;
  steps: Record<string, PlanTap>;
  /** Proof taps. A "seen" answer moves them to `history` (kept for the binder) and the way in is asked again. */
  proofs: Record<string, PlanTap>;
  history: (PlanTap & { step: string })[];
  /** When the daily look was turned on from here (the checklist item's id). */
  watchOn?: PlanTap & { taskId: string };
  answers: PlanAnswer[];
  /** "Posted" taps on the printouts tied to this item (quickwins.ts print wins), by printout id. */
  posted?: Record<string, PlanTap>;
  updatedAt: string;
}

const codeKey = (code: string) => code.trim().replace(/\*+\s*$/, "").trim().toUpperCase();
export const citedPlanId = (locationId: string, planId: string, citedOn: string, code: string) => `${locationId}~${planId}~${citedOn}~${codeKey(code)}`;

export function newPlanDoc(o: { orgId: string; locationId: string; planId: string; code: string; citedOn: string; at: string }): CitedPlanDoc {
  return { id: citedPlanId(o.locationId, o.planId, o.citedOn, o.code), orgId: o.orgId, locationId: o.locationId, planId: o.planId, code: o.code, citedOn: o.citedOn, startedAt: o.at, steps: {}, proofs: {}, history: [], answers: [], updatedAt: o.at };
}

export type PlanAct =
  | { act: "step"; step: string; done: boolean }
  /** "All fixed today": every "Fix it today" step in one tap (each keeps its own who and when). */
  | { act: "today"; done: boolean }
  /** A printout tied to the item is up: "Posted". */
  | { act: "posted"; print: string; done: boolean }
  | { act: "proof"; step: string; done: boolean }
  | { act: "recheck"; answer: "clear" | "seen" };

/** A tap the plan can't take (its words are for the person). */
export class PlanActError extends Error {}

/** One tap applied (pure). Throws a plain message for a step the plan doesn't have or an answer not asked yet. */
export function applyPlanAct(def: CitedPlanDef, doc: CitedPlanDoc, a: PlanAct, who: { by: string; byName: string }, at: string): CitedPlanDoc {
  const tap: PlanTap = { at, by: who.by, byName: who.byName };
  const d: CitedPlanDoc = structuredClone(doc);
  d.updatedAt = at;
  if (a.act === "today") {
    for (const s of def.now) { if (a.done) d.steps[s.id] ??= tap; else delete d.steps[s.id]; }
    return d;
  }
  if (a.act === "posted") {
    if (!/^[a-z]+:[A-Za-z0-9_.-]{1,60}$/.test(a.print)) throw new PlanActError("That printout isn't in this plan.");
    d.posted ??= {};
    if (a.done) d.posted[a.print] ??= tap; else delete d.posted[a.print];
    return d;
  }
  if (a.act === "step") {
    if (!def.now.some((s) => s.id === a.step)) throw new PlanActError("That step isn't in this plan.");
    if (a.done) d.steps[a.step] ??= tap; else delete d.steps[a.step];
    return d;
  }
  if (a.act === "proof") {
    if (!def.source.some((s) => s.id === a.step)) throw new PlanActError("That step isn't in this plan.");
    if (a.done) d.proofs[a.step] ??= tap; else delete d.proofs[a.step];
    return d;
  }
  const r = recheckState(def, d, at.slice(0, 10));
  if (!r.due) throw new PlanActError(r.closed ? "This one is closed. The daily look still asks." : `The next check-back is ${r.nextText}.`);
  d.answers.push({ ...tap, answer: a.answer });
  if (a.answer === "seen") {
    for (const [step, t] of Object.entries(d.proofs)) d.history.push({ ...t, step });
    d.proofs = {};
  }
  return d;
}

// ── Dates ────────────────────────────────────────────────────────────────────────────────────────────────────────────────

const addDays = (date: string, n: number) => { const t = new Date(`${date.slice(0, 10)}T12:00:00Z`); t.setUTCDate(t.getUTCDate() + n); return t.toISOString().slice(0, 10); };

/** Where the check-back stands: closed after `clearRun` "No flies" in a row; else the next date, from the last "I saw" or the start. */
export function recheckState(def: CitedPlanDef, doc: Pick<CitedPlanDoc, "startedAt" | "answers">, today: string): { closed: boolean; clearSince: string | null; next: string | null; nextText: string | null; due: boolean; lastSeen: string | null } {
  let run = 0;
  for (let i = doc.answers.length - 1; i >= 0 && doc.answers[i]!.answer === "clear"; i--) run++;
  const lastSeen = [...doc.answers].reverse().find((x) => x.answer === "seen")?.at ?? null;
  const clearSince = run ? doc.answers[doc.answers.length - run]!.at.slice(0, 10) : null;
  if (run >= def.recheck.clearRun) return { closed: true, clearSince, next: null, nextText: null, due: false, lastSeen };
  const from = (lastSeen ?? doc.startedAt).slice(0, 10);
  const next = addDays(from, def.recheck.days[Math.min(run, def.recheck.days.length - 1)]!);
  return { closed: false, clearSince, next, nextText: shortDate(next), due: today >= next, lastSeen };
}

// ── The view ─────────────────────────────────────────────────────────────────────────────────────────────────────────────

/** A step's "Get it" link: the plain name, the Amazon search, the line on where it goes, and the count's control name. */
export interface PlanSupplyView { name: string; url: string; note: string | null; cc: string }
export interface PlanStepView { id: string; say: string; done: PlanTap | null; law: { cite: string; quote: string } | null; advice: string[]; yes?: string; /** Empty for a step with nothing to buy. */ supplies: PlanSupplyView[]; /** Guidance neither law nor ours (FDA's guide for inspectors). */ basis?: string }
export interface CitedPlanView {
  planId: string;
  code: string;
  thing: string;
  title: string;
  /** The library topic with the steps in full: "Flies inside right now" (/learn/library/<id>). */
  guide: { label: string; to: string };
  /** Cited at more than one inspection we hold: the source part comes first. */
  repeat: boolean;
  order: PlanPart[];
  now: { heading: string; steps: PlanStepView[]; done: number; reward: string | null };
  source: { heading: string; lede: string; steps: PlanStepView[]; done: number; reopened: string | null; reward: string | null };
  watch: { heading: string; lines: string[]; offer: { label: string } | null };
  recheck: { heading: string; ask: { question: string; clear: string; seen: string } | null; next: string | null; answers: string[]; closed: string | null; seenLine: string | null };
  /** The one next thing, for Today's quiet row: the check-back question, "Flies plan: 2 of 4 fixes done", or the ready plan. Null when nothing is left. */
  today: { line: string; action: string } | null;
  /** The binder's trail, facts only. */
  trail: string[];
  /** SUPPLY_NOTE, said once, under the first step (in the plan's order) with a "Get it" line: `at` is "now:<id>" or "source:<id>". */
  supplyNote: { line: string; at: string } | null;
  /** Item plans (absent on the flies plan, which keeps its shipped shape): the kind, the page anchor, the FDA item, "Ask in CleanCleaver". */
  kind?: PlanKind;
  anchor?: string;
  item?: number;
  /** "Ask in CleanCleaver": `q` is the question to ask (the department's words); `to` opens Ask on the page. */
  ask?: { label: string; to: string; q: string };
  /** Printouts marked "Posted" for this item (quickwins.ts print wins), by printout id. Absent when none. */
  posted?: string[];
}

export const PLAN_HEADINGS = { now: "Fix it today", source: "Fix where they get in", watch: "Watch for it", recheck: "Check back" } as const;
export const planTitle = (d: CitedPlanDef) => d.title ?? `Your plan for the ${d.thing}`;
export const LAW_LABEL = "The law";
/** "The law: Food Code 3-501.16(A)(2): “quote”": the quote only when there is one (a step with no section has no law line at all). */
export const planLawLine = (law: { cite: string; quote: string }): string => `${LAW_LABEL}: ${law.cite}${law.quote ? `: “${law.quote}”` : ""}`;
export const ADVICE_LABEL = "Our advice, beyond the law";
const REPEAT_LEDE = "Cited more than once, so start here: find where they get in.";
const SOURCE_LEDE = "Tap yes once you've looked. It goes in your binder.";
const capital = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** The flies plan's words, exactly as they shipped (owner, 2026-10-13). */
export const FLIES_TEXT: PlanText = {
  sourceHeading: PLAN_HEADINGS.source, repeatLede: REPEAT_LEDE, nextSource: "Next: find where they get in.", sourceDone: "The way in is closed",
  ready: (n) => `Flies were cited. Your ${n}-step plan is ready`,
  nowProgress: (d, t) => `Flies plan: ${d} of ${t} fixes done`,
  sourceProgress: (d, t) => `Flies plan: ${d} of ${t} ways in looked at`,
  trailSource: "Way in checked", trailHistory: "Checked before flies came back", seenAnswer: "flies seen",
  closedSince: (date) => `No flies reported since ${date}.`,
  closedTail: (daily) => (daily ? "The daily look keeps asking." : "The weekly walk-through keeps asking."),
  handoff: { lede: "Flies were cited. A plan is ready in the app, one tap per step:", then: "Then: check where they get in, a daily fly look, and a check-back in 7 days." },
};
const textOf = (d: CitedPlanDef): PlanText => d.text ?? FLIES_TEXT;

/** A Food Code section at this place: Utah's adoption line, plus the county's own code number where its code book maps it. */
export function planLawAt(cite: string, place: PlaceRef & { sourceId?: string }): string {
  const at = walkRuleAt(cite, place).cite;
  const sec = /\d-\d{3}\.\d+/.exec(cite)?.[0];
  const county = sec && place.sourceId ? countyCodeLine(place.sourceId, sec) : null;
  const state = sec ? stateLawLine(place.state, sec) : null;
  return [at, county, state].filter(Boolean).join(". ");
}

const tapText = (t: PlanTap) => `${shortDate(t.at)}${t.byName ? `, ${t.byName}` : ""}`;

export function citedPlanView(def: CitedPlanDef, o: { code: string; citedOn: string; repeat: boolean; doc: CitedPlanDoc | null; place: PlaceRef & { sourceId?: string }; watchOn: readonly string[]; dailyTask: boolean; today: string }): CitedPlanView {
  const doc = o.doc;
  const tx = textOf(def);
  const stepView = (s: PlanStepDef | PlanProofDef, done: PlanTap | undefined): PlanStepView => ({
    id: s.id, say: s.say, done: done ?? null,
    law: s.law ? { cite: planLawAt(s.law.cite, o.place), quote: s.law.quote ?? "" } : null,
    advice: (s.advice ?? []).map((a) => a.say),
    ...("yes" in s ? { yes: s.yes } : {}),
    supplies: (s.supplies ?? []).map((x) => ({ name: x.name, url: supplyUrl(x.query), note: x.note ?? null, cc: supplyControl(s.id) })),
    ...(s.basis ? { basis: s.basis } : {}),
  });
  const now = def.now.map((s) => stepView(s, doc?.steps[s.id]));
  const src = def.source.map((s) => stepView(s, doc?.proofs[s.id]));
  const nowDone = now.filter((s) => s.done).length, srcDone = src.filter((s) => s.done).length;
  const r = doc ? recheckState(def, doc, o.today) : null;
  const seen = !!r?.lastSeen && !r.closed && srcDone < src.length;
  const watchLines = [...new Set(def.watch.base.filter((b) => o.watchOn.includes(b.key)).map((b) => b.line))];
  if (o.dailyTask) watchLines.unshift(def.watch.add.line);
  if (def.watch.builtin) watchLines.unshift(def.watch.builtin);
  const daily = o.dailyTask || !!def.watch.builtin || def.watch.daily.some((k) => o.watchOn.includes(k));
  const nextText = r?.nextText ?? null;
  const order: PlanPart[] = o.repeat ? ["source", "now", "watch", "recheck"] : ["now", "source", "watch", "recheck"];
  const nextAfterNow = order.indexOf("source") > order.indexOf("now") ? tx.nextSource : nextText ? `Next: we'll ask on ${nextText}.` : "Next: watch for them.";
  const view: CitedPlanView = {
    planId: def.id, code: o.code, thing: def.thing, title: planTitle(def), guide: { label: def.guide.section, to: def.guide.id.startsWith("/") ? def.guide.id : `/learn/library/${def.guide.id}` }, repeat: o.repeat, order,
    now: { heading: PLAN_HEADINGS.now, steps: now, done: nowDone, reward: nowDone === now.length ? `Today's fixes are done. ${nextAfterNow}` : null },
    source: {
      heading: tx.sourceHeading, lede: o.repeat ? tx.repeatLede : SOURCE_LEDE, steps: src, done: srcDone,
      reopened: seen ? def.recheck.seenLine : null,
      reward: srcDone === src.length ? (nextText ? `${tx.sourceDone}. Next: we'll ask on ${nextText}.` : `${tx.sourceDone}.`) : null,
    },
    watch: { heading: PLAN_HEADINGS.watch, lines: watchLines, offer: daily ? null : { label: def.watch.add.button } },
    recheck: {
      heading: PLAN_HEADINGS.recheck,
      ask: r?.due ? { question: def.recheck.question, clear: def.recheck.clear, seen: def.recheck.seen } : null,
      next: !r ? `We'll ask "${def.recheck.question}" ${def.recheck.days[0]} days after your first tap.` : r.closed ? null : r.due ? null : `Next check-back: ${r.nextText}.`,
      answers: (doc?.answers ?? []).map((x) => `${shortDate(x.at)}: ${x.answer === "clear" ? def.recheck.clear.toLowerCase() : tx.seenAnswer}`),
      closed: r?.closed && r.clearSince ? `${tx.closedSince(shortDate(r.clearSince))} ${tx.closedTail(daily)}` : null,
      seenLine: seen ? def.recheck.seenLine : null,
    },
    today: null,
    trail: [],
    supplyNote: null,
  };
  if (def.kind && def.kind !== "rich") {
    view.kind = def.kind;
    view.anchor = planAnchor(def, o.code);
    if (def.item != null) view.item = def.item;
    if (def.ask) view.ask = { ...def.ask };
  }
  if (doc?.posted && Object.keys(doc.posted).length) view.posted = Object.keys(doc.posted).sort();
  for (const p of order) {
    if (p !== "now" && p !== "source") continue;
    const first = (p === "now" ? now : src).find((x) => x.supplies.length);
    if (first) { view.supplyNote = { line: SUPPLY_NOTE, at: `${p}:${first.id}` }; break; }
  }
  view.today = r?.due ? { line: def.recheck.question, action: "Answer" }
    : r?.closed ? null
    : !doc ? { line: tx.ready(now.length), action: "Open it" }
    : nowDone < now.length ? { line: tx.nowProgress(nowDone, now.length), action: "Open it" }
    : srcDone < src.length ? { line: tx.sourceProgress(srcDone, src.length), action: "Open it" }
    : null;
  view.trail = planTrail(def, o, doc, r, watchLines);
  return view;
}

/** The page anchor a plan opens at ("Let's fix that", Today's first card): "plan-flies" for the flies plan, else one per cited code. */
export function planAnchor(def: Pick<CitedPlanDef, "id" | "kind">, code: string): string {
  if (!def.kind || def.kind === "rich") return `plan-${def.id}`;
  return `plan-${code.trim().replace(/\*+\s*$/, "").replace(/[^A-Za-z0-9]+/g, "-").replace(/^-|-$/g, "").toLowerCase() || "item"}`;
}
/** The anchor of a plan's view (the flies plan's view has none of its own: "plan-flies"). */
export const anchorOf = (v: Pick<CitedPlanView, "anchor" | "planId">) => v.anchor ?? `plan-${v.planId}`;

/** The plan's part 1 is done: every "Fix it today" step tapped. What "fixed" counts ("1 of 4 fixed"). */
export const planFixed = (v: Pick<CitedPlanView, "now">): boolean => v.now.steps.length > 0 && v.now.done === v.now.steps.length;
/** "1 of 4 fixed". */
export const fixedLine = (fixed: number, total: number) => `${fixed} of ${total} fixed`;

/** The binder's lines (no arrows: the PDF's font has none). Only what the taps and answers say. */
function planTrail(def: CitedPlanDef, o: { code: string; citedOn: string }, doc: CitedPlanDoc | null, r: ReturnType<typeof recheckState> | null, watchLines: string[]): string[] {
  const head = `${capital(def.kind === "fallback" ? "this item" : def.thing)} (${o.code}), cited ${shortDate(o.citedOn)}`;
  if (!doc) return [`${head}: plan not started.`];
  const out = [`${head}: plan started ${shortDate(doc.startedAt)}.`];
  const steps = def.now.filter((s) => doc.steps[s.id]);
  out.push(`Fixed today: ${steps.length} of ${def.now.length}${steps.length ? ` (${steps.map((s) => `${s.say.replace(/[.:].*$/, "").toLowerCase()}, ${tapText(doc.steps[s.id]!)}`).join("; ")})` : ""}.`);
  const proofs = def.source.filter((s) => doc.proofs[s.id]);
  const tx = textOf(def);
  out.push(`${tx.trailSource}: ${proofs.length} of ${def.source.length}${proofs.length ? ` (${proofs.map((s) => `${s.yes.toLowerCase()}, ${tapText(doc.proofs[s.id]!)}`).join("; ")})` : ""}.`);
  if (doc.history.length) out.push(`${tx.trailHistory}: ${doc.history.map((h) => `${def.source.find((s) => s.id === h.step)?.yes.toLowerCase() ?? h.step}, ${tapText(h)}`).join("; ")}.`);
  if (watchLines.length) out.push(`Watched: ${watchLines.map((l) => l.replace(/^We'll ask about /, "").replace(/\.$/, "")).join("; ")}.`);
  if (doc.answers.length) out.push(`Check-backs: ${doc.answers.map((x) => `${shortDate(x.at)} ${x.answer === "clear" ? def.recheck.clear.toLowerCase() : tx.seenAnswer}`).join("; ")}.`);
  if (r?.closed && r.clearSince) out.push(tx.closedSince(shortDate(r.clearSince)));
  return out;
}

/** A plan about a worker's health says nothing of it in a notice (owner rule: no health details in any notification). */
export const QUIET_HANDOFF = "An item from your last inspection has a fix plan ready in the app, one tap per step.";
/** The prep hand-off preview's lines (no taps there: the owner taps them in the app). */
export function handoffPlanOf(def: CitedPlanDef): { lede: string; steps: string[]; then: string } {
  const tx = textOf(def);
  if (def.quiet) return { lede: QUIET_HANDOFF, steps: [], then: "" };
  return { lede: tx.handoff.lede, steps: def.now.map((s) => s.say), then: tx.handoff.then };
}

/** Every string we write in a plan (the copy tests read it). */
export function planWords(def: CitedPlanDef): string[] {
  return [
    planTitle(def), ...Object.values(PLAN_HEADINGS), REPEAT_LEDE, SOURCE_LEDE, LAW_LABEL, ADVICE_LABEL,
    ...(def.text ? [def.text.sourceHeading, def.text.repeatLede, def.text.nextSource, def.text.sourceDone, def.text.ready(3), def.text.nowProgress(1, 3), def.text.sourceProgress(1, 3), def.text.closedSince("Oct 3"), def.text.closedTail(true), def.text.closedTail(false), def.text.handoff.lede, def.text.handoff.then] : []),
    ...[...def.now, ...def.source].map((s) => s.basis ?? ""), def.watch.builtin ?? "", def.ask?.label ?? "",
    ...def.now.flatMap((s) => [s.say, ...(s.advice ?? []).map((a) => a.say)]), ...def.source.flatMap((s) => [s.say, s.yes, ...(s.advice ?? []).map((a) => a.say)]),
    ...def.watch.base.map((b) => b.line), def.watch.add.title, def.watch.add.how, def.watch.add.line, def.watch.add.button,
    def.recheck.question, def.recheck.clear, def.recheck.seen, def.recheck.seenLine,
    ...Object.values(handoffPlanOf(def)).flat(),
    ...[...def.now, ...def.source].flatMap((s) => (s.supplies ?? []).flatMap((x) => [`${SUPPLY_LEAD} ${x.name}`, x.note ?? ""])),
    ...([...def.now, ...def.source].some((s) => s.supplies?.length) ? [SUPPLY_NOTE] : []),
  ].filter(Boolean);
}

// ── Every other cited item: its plan, from core fixplans.ts (owner, 2026-10-14: "It should do anything") ──────────────────
//
// The same four parts as flies, built from ONE source (fixplans.ts ITEM_FIXES, which the articles read too):
//   now      the situation's fix-now steps when the inspector's note names one (violationnotes.ts matchNote, a phrase match);
//            else the item's general plan: the first step of each situation, at most 4.
//   source   the item's "fix where it comes from" lines, then its proof line, then the licensed trade's line; one yes tap each.
//   watch    the app's real check where one exists (lastinspection.ts INSPECTION_COVER: built-in, flow or checklist item); else a
//            one-tap "Add it" weekly look (ASSUMED cadence, labeled here).
//   recheck  7, then 30 days.
// Every step keeps its Food Code section (law), and a step that only our library backs is labeled our advice; FDA's inspector guide
// is said as its own line (neither law nor ours). A state's adoption and the county's own code number come only from
// planLawAt → roomwalk.ts walkRuleAt / states/ut/official.ts countyCodeLine (never written here).
// No item at all (a department code no table places): the fallback plan from the citation's own words, never a dead end.

/** What keeps an item right here (lastinspection.ts CoverDef, structurally): passed in, so this file never imports that one. */
export interface PlanCover {
  base?: { keys: readonly string[]; label: string } | readonly { keys: readonly string[]; label: string }[];
  builtin?: string;
  titles?: readonly string[];
  add?: { title: string; how: string; category: TaskCategory; schedule: { perShift?: true; everyDays?: number }; requirementIds: string[] };
}
export interface PlanCtx { cover?: PlanCover; guide?: { id: string; title: string } | null }

const FC_SEC = /^FC (\d-\d{3}\.\d+(?:\([A-Za-z0-9]+\))*)/;
const fcOf = (cites: readonly string[]): string | undefined => cites.map((c) => FC_SEC.exec(c)?.[1]).find((x): x is string => !!x);
const OURS = "This step is our advice, beyond the law.";
export const ITEM_PLAN_YES = "Yes, done";
export const ITEM_PROOF_YES = "Yes, it's kept";

/**
 * "Get it" only where the step itself names the thing (owner: "only where the article names a supply"). A plain name and an Amazon
 * search; SUPPLY_NEVER is the lint (no pesticide, bait, spray or insect light), the same as the flies plan's.
 */
export const ITEM_SUPPLIES: readonly { match: RegExp; supply: PlanSupply }[] = [
  { match: /\btest strips?\b|\bstrips?\b.{0,20}\bsanitiz/i, supply: { name: "Sanitizer test strips", query: "sanitizer test strips chlorine quat food service", note: "Match the strip to your sanitizer." } },
  { match: /\bthermometers?\b/i, supply: { name: "Food thermometer", query: "food probe thermometer" } },
  { match: /\bpaper towels?\b/i, supply: { name: "Paper towels for the hand sink", query: "multifold paper towels" } },
  { match: /\bsoap\b/i, supply: { name: "Hand soap", query: "hand soap refill commercial" } },
  { match: /\bhair (?:nets?|restraints?)\b|\bhairnets?\b/i, supply: { name: "Hair nets", query: "hair nets food service" } },
  { match: /\bgloves?\b/i, supply: { name: "Food service gloves", query: "food service gloves disposable" } },
  { match: /\bshallow pans?\b/i, supply: { name: "Shallow pans", query: "shallow hotel pans 2 inch" } },
  { match: /\bday ?dots?\b|\blabels?\b.{0,30}\b(?:use[- ]by|date)\b|\b(?:use[- ]by|date)\b.{0,30}\blabels?\b/i, supply: { name: "Food date labels", query: "food date labels removable" } },
];
const suppliesOf = (say: string): PlanSupply[] | undefined => {
  const xs = ITEM_SUPPLIES.filter((x) => x.match.test(say)).map((x) => x.supply);
  return xs.length ? xs : undefined;
};

const FORM_FILE = "research/paa/inspection-form-items.txt";
/**
 * The Food Code's words for each section a plan cites (fcquotes.ts, about 20 kB). Registered by the server (services/api/src/official/
 * lastinspection.ts imports that file), never imported here, so the web bundle (whose demo builds plans in the browser) does not carry
 * them: a plan built without them shows each law line by its cite alone, never with an empty quote.
 */
let QUOTES: { quotes: Readonly<Record<string, string>>; files: Readonly<Record<string, string>> } = { quotes: {}, files: {} };
export function registerFcQuotes(q: typeof QUOTES): void { QUOTES = q; }
/** The law line for a Food Code section or paragraph ("3-501.16(A)(2)"): the exact entry's words, else the section's, else the cite alone. */
const lawOf = (sec: string): PlanLaw => {
  const base = QUOTES.quotes[sec] !== undefined ? sec : sec.replace(/\(.*$/, "");
  const quote = QUOTES.quotes[base];
  return { cite: `Food Code ${sec}`, ...(quote ? { quote, quoteFile: QUOTES.files[base] ?? FORM_FILE } : {}) };
};

function stepOf(id: string, l: FixLine): PlanStepDef {
  // A step shows "The law" only when its OWN line cites a Food Code section (never the item's section lent to a step that only our
  // library or FDA's guide backs: run-17 P1-1). The quote is the Code's words for that section or paragraph, when we hold them.
  const own = fcOf(l.cite);
  const sec = own;
  const g3b = l.cite.map((c) => /^G3B (\d+)$/.exec(c)?.[1]).find((x): x is string => !!x);
  const ours = !own && l.cite.some((c) => c.startsWith("LIB "));
  const supplies = suppliesOf(l.say);
  return {
    id, say: l.say,
    ...(sec ? { law: lawOf(sec) } : {}),
    ...(ours ? { advice: [{ say: OURS }] } : {}),
    ...(g3b ? { basis: `From FDA's guide for inspectors, item ${g3b}.` } : {}),
    ...(supplies ? { supplies } : {}),
  };
}
const proofOf = (id: string, l: FixLine, yes: string): PlanProofDef => ({ ...stepOf(id, l), yes });

const slugOf = (s: string) => s.toLowerCase().replace(/°f/g, "f").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
const bases = (c: PlanCover | undefined): { keys: readonly string[]; label: string }[] => (!c?.base ? [] : Array.isArray(c.base) ? [...c.base] : [c.base as { keys: readonly string[]; label: string }]);

/** Items about a worker's health (3 employee health reporting, 4 sick worker exclusion). */
const HEALTH_ITEMS: ReadonlySet<number> = new Set([3, 4]);

/** The words an item plan says around its steps. */
export function itemPlanText(topic: string): PlanText {
  const T = capital(topic);
  return {
    sourceHeading: "Fix where it comes from", repeatLede: "Cited more than once, so start here: fix where it comes from.",
    nextSource: "Next: fix where it comes from.", sourceDone: "Where it comes from is fixed",
    ready: (n) => `${T} was cited. Your ${n}-step plan is ready`,
    nowProgress: (d, t) => `${T}: ${d} of ${t} fixes done`,
    sourceProgress: (d, t) => `${T}: ${d} of ${t} causes fixed`,
    trailSource: "Cause fixed", trailHistory: "Fixed before it came back", seenAnswer: "seen again",
    closedSince: (date) => `Nothing seen again since ${date}.`,
    closedTail: (daily) => (daily ? "Your daily check keeps watching." : "Your checks keep watching."),
    handoff: { lede: `${T} was cited. A plan is ready in the app, one tap per step:`, then: "Then: fix where it comes from, a check that watches for it, and a check-back in 7 days." },
  };
}

/**
 * One item's plan. `situation`: the situation's name (matchNote picked it from the inspector's note), or null for the general plan.
 * `ctx`: the item's cover and library guide (lastinspection.ts INSPECTION_COVER, ITEM_GUIDE).
 */
export function itemPlanDef(n: number, situation: string | null, ctx: PlanCtx = {}): CitedPlanDef | null {
  const fix = itemFixOf(n);
  if (!fix) return null;
  const sit = situation ? fix.situations.find((s) => s.name === situation) ?? null : null;
  const lines: FixLine[] = [];
  if (sit) lines.push(...sit.steps);
  else for (const s of fix.situations) { const first = s.steps[0]; if (first && !lines.some((x) => x.say === first.say)) lines.push(first); }
  const now = lines.slice(0, sit ? 6 : 4).map((l, i) => stepOf(`n${i + 1}`, l));
  const source: PlanProofDef[] = [
    ...fix.source.map((l, i) => proofOf(`c${i + 1}`, l, ITEM_PLAN_YES)),
    proofOf("proof", fix.proof, ITEM_PROOF_YES),
    ...(fix.pro ? [proofOf("pro", fix.pro, ITEM_PLAN_YES)] : []),
  ];
  const c = ctx.cover;
  const bs = bases(c);
  const add = c?.add ?? { title: `Weekly look: ${fix.topic}`, how: fix.law.say, category: "maintenance" as TaskCategory, schedule: { everyDays: 7 }, requirementIds: [] };
  const lib = [...fix.situations.flatMap((x) => x.steps), ...fix.source, fix.proof, ...(fix.advice ? [fix.advice] : [])].flatMap((l) => l.cite).find((x) => x.startsWith("LIB "))?.slice(4);
  const guide = ctx.guide ? { id: ctx.guide.id, section: ctx.guide.title } : lib ? { id: lib, section: "Our guide on this" } : { id: `/learn/library?q=${encodeURIComponent(fix.topic)}`, section: "Look it up in the library" };
  return {
    id: sit ? `i${n}.${slugOf(sit.name)}` : `i${n}`, kind: sit ? "situation" : "general", item: n, situation: sit?.name ?? null,
    ...(HEALTH_ITEMS.has(n) ? { quiet: true as const } : {}),
    items: [n], words: /[\s\S]*/, thing: fix.topic, title: `Your plan: ${fix.topic}`, guide, now, source,
    watch: {
      base: bs.flatMap((b) => b.keys.map((key) => ({ key, line: `We'll keep checking it: ${b.label}.` }))),
      daily: bs.flatMap((b) => [...b.keys]),
      ...(c?.builtin ? { builtin: `Covered: ${c.builtin}.` } : {}),
      ...(c?.titles?.length ? { titles: c.titles } : {}),
      add: { ...add, line: `We'll ask on your checklist: ${add.title}.`, button: `Add it: ${add.title}` },
    },
    recheck: {
      days: [7, 30], question: `Any ${fix.topic} problem this week?`, clear: "No, all good", seen: "Yes, I saw it", clearRun: 2,
      seenLine: `It came back, so where it comes from needs another look.${fix.pro ? ` ${fix.pro.say}` : ""}`,
    },
    text: itemPlanText(fix.topic),
  };
}

/** The fallback's one section (in docs/source/research/paa/inspection-form-items.txt): the person in charge's duties. */
export const FALLBACK_LAW = "Food Code 2-103.11";
export const ASK_LABEL = "Ask in CleanCleaver";
const cut = (s: string, n: number) => (s.length <= n ? s : `${s.slice(0, n - 1).replace(/\s+\S*$/, "")}…`);

/**
 * A code no table places, or an item with no fix (never today: every item 1-56 has one, test-checked): a plan from the citation's
 * own words, so the person is never left at a dead end (owner, 2026-10-14). Tell the person in charge, fix it today, find the cause,
 * ask in CleanCleaver, add a check. The API also logs a missing-plan gap for the team, so it gets a real plan.
 */
export function fallbackPlanDef(words: { title: string; detail: string | null }): CitedPlanDef {
  const what = cut((words.title || "this item").trim(), 60);
  const law = lawOf(FALLBACK_LAW.replace(/^Food Code /, ""));
  return {
    id: "x", kind: "fallback", situation: null, items: [], words: /[\s\S]*/, thing: "it", title: `Your plan: ${what}`,
    guide: { id: `/learn/library?q=${encodeURIComponent(what)}`, section: "Look it up in the library" },
    ask: { label: ASK_LABEL, to: "/inspection?ask=1", q: cut(`How do I fix this inspection item: ${words.title}${words.detail ? `. ${words.detail}` : ""}`, 200) },
    now: [
      { id: "n1", say: "Tell the person in charge what was cited.", law },
      { id: "n2", say: "Fix what the inspector wrote, today.", advice: [{ say: OURS }] },
    ],
    source: [
      { id: "c1", say: "Find why it happened, and fix that.", yes: ITEM_PLAN_YES, advice: [{ say: OURS }] },
      { id: "c2", say: "Ask in CleanCleaver how to keep it fixed.", yes: "Yes, asked", advice: [{ say: OURS }] },
    ],
    watch: {
      base: [], daily: [],
      add: { title: cut(`Check: ${what}`, 60), how: "Look at what the inspector cited. Still right? Tap done.", category: "maintenance", schedule: { everyDays: 7 }, requirementIds: [], line: "We'll ask on your checklist every week.", button: "Add a weekly check" },
    },
    recheck: { days: [7, 30], question: "Has it come back this week?", clear: "No, all good", seen: "Yes, it's back", clearRun: 2, seenLine: "It came back, so the cause needs another look." },
    text: itemPlanText("this item"),
  };
}

/**
 * The plan for one cited item, never null (owner, 2026-10-14: "each violation has an answer"): the richer plan when its words pick
 * one (flies), else the item's plan (the situation the inspector's note names, or the item's general plan), else the fallback.
 */
export function citedPlanOf(items: readonly number[], words: { title: string; detail: string | null }, ctxOf: (n: number) => PlanCtx = () => ({})): CitedPlanDef {
  const rich = citedPlanFor(items, words);
  if (rich) return rich;
  for (const n of items) {
    if (!itemFixOf(n)) continue;
    const m = matchNote({ item: n, note: words.detail?.trim() || words.title });
    const def = itemPlanDef(n, m.strength === "strong" && m.situation ? m.situation.name : null, ctxOf(n));
    if (def) return def;
  }
  // No table places the code: the inspector's own words may still name a situation (a phrase match only, never word overlap).
  if (!items.length) {
    const m = matchNote({ item: null, note: words.detail?.trim() || words.title });
    if (m.strength === "strong" && m.item != null && m.situation) {
      const def = itemPlanDef(m.item, m.situation.name, ctxOf(m.item));
      if (def) return def;
    }
  }
  return fallbackPlanDef(words);
}

/**
 * The team's "missing plan" gap (owner, 2026-10-14): a cited code that got only the generic plan (kind "fallback") becomes a content
 * ticket, counted under the department, never a business, with the department's words anonymized. The same words join one ticket
 * (counted once per department: the ask is `once`, so a page read never raises it again on any instance); a drafted plan then covers the code. Null for any other plan kind or an empty citation.
 */
export function planGapAsk(o: { kind: PlanKind | undefined; code: string; title: string; detail: string | null; sourceId: string; area: string; at: string; names?: readonly string[]; addresses?: readonly string[] }): TicketAsk | null {
  if (o.kind !== "fallback") return null;
  const clean = anonymizeNote(`${o.title}${o.detail ? `. ${o.detail}` : ""}`, { names: o.names, addresses: o.addresses }).trim();
  if (clean.length < 3) return null;
  return {
    question: `Missing fix plan, ${o.sourceId} code ${o.code.replace(/\*+$/, "")}: ${clean}`,
    orgId: `inspection:${o.sourceId}`, kind: "business", at: o.at, inScope: true, once: true,
    reason: `No Form 3-A item places this ${o.area} code, so the citation got the generic plan. Add its item or a plan.`, picks: [],
  };
}
